import React, {useCallback, useState} from 'react';
import {
  Box,
  HStack,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import {InfoIcon} from '@chakra-ui/icons';
import {Navigable} from '@synako/halitrek';
import {DisponibiliteArticleDesc} from 'api-types/ffconnect-hal';
import {Trans, useTranslation} from 'react-i18next';
import {dateAvecSlashes} from 'ff-common';
import {Unite} from 'api-types/ffconnect';
import {useHalState} from '@synako/haligator';

interface ArticleInfoTooltipProps {
  dispo?: Navigable<DisponibiliteArticleDesc>;
  itemUniteDeCommande?: Unite | undefined;
  isBackoffice?: boolean;
}

/**
 * Composant qui affiche un tooltip avec les infos de DLC et de stock
 * Visible uniquement en backoffice
 */
const ArticleInfoTooltipComponent: React.FC<ArticleInfoTooltipProps> = ({
  dispo,
  itemUniteDeCommande,
  isBackoffice = false,
}) => {
  const {t} = useTranslation();
  // Etat pour savoir si on doit charger la disponibilité
  const [loadDispo, setLoadDispo] = useState(false);
  // On contrôle le tooltip manuellement pour éviter qu'il ne s'affiche lors d'un focus programmatique
  const {isOpen, onOpen, onClose} = useDisclosure();

  // Fonction pour ouvrir le tooltip
  const openTooltip = useCallback(() => {
    setLoadDispo(true);
    onOpen();
  }, [onOpen]);

  // Survol volontaire à la souris → ouverture du tooltip
  const handleMouseEnter = useCallback(() => {
    openTooltip();
  }, [openTooltip]);

  // Sortie de la souris → on referme pour empêcher qu'il reste visible.
  const handleMouseLeave = useCallback(() => {
    onClose();
  }, [onClose]);

  // Hook pour charger la disponibilité uniquement si loadDispo est true
  const dispoState = useHalState(loadDispo && dispo ? dispo : undefined);

  // Si on n'est pas en backoffice ou qu'on n'a pas de dispo, on n'affiche rien
  if (!isBackoffice || !dispo) {
    return null;
  }

  return (
    <Tooltip
      borderRadius='md'
      boxShadow='md'
      placement='top'
      hasArrow
      isOpen={isOpen}
      label={
        dispoState.loading ? (
          <Text>{t('global.chargement')}</Text>
        ) : dispoState.data &&
          (dispoState.data.tolede.dlc_envisagee ??
            dispoState.data.tolede.quantite_physique) ? (
          <VStack
            align='flex-start'
            spacing={1}
            p={1}
          >
            {dispoState.data.tolede.dlc_envisagee && (
              <HStack fontWeight='bold'>
                <Text>
                  <Trans>
                    {t('ligne_produit.encart_dlc', {
                      date: dateAvecSlashes(
                        dispoState.data.tolede.dlc_envisagee,
                      ),
                    })}
                  </Trans>
                </Text>
              </HStack>
            )}
            {dispoState.data.tolede.quantite_physique && (
              <HStack>
                <Text fontWeight='bold'>
                  {t('back_office.prise_de_commande.panier.stock', {
                    count: dispoState.data.tolede.quantite_physique,
                    unite: t(`unite.${itemUniteDeCommande}`, {
                      count: dispoState.data.tolede.quantite_physique,
                    }),
                  })}
                </Text>
              </HStack>
            )}
          </VStack>
        ) : (
          <Text>
            {t('back_office.prise_de_commande.panier.dlc_stock_sans_donnees')}
          </Text>
        )
      }
    >
      <Box
        ml={1}
        display='inline-flex'
        cursor='pointer'
        tabIndex={0}
        role='button'
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <InfoIcon
          boxSize={4}
          color='gray.dark'
          bg='white'
          borderRadius='full'
          p={0.5}
        />
      </Box>
    </Tooltip>
  );
};

// Mémoïsation pour éviter les rerenders inutiles
export const ArticleInfoTooltip = React.memo(ArticleInfoTooltipComponent);
